import { Link } from 'react-router-dom';

export interface Crumb {
  label: string;
  to?: string;
}

interface BreadcrumbsProps {
  items: Crumb[];
}

/**
 * Breadcrumbs — lightweight navigation trail.
 *
 * Usage:
 *   <Breadcrumbs items={[
 *     { label: 'Dashboard', to: '/dashboard' },
 *     { label: 'Clients', to: '/dashboard/clients' },
 *     { label: 'Acme Corp' },
 *   ]} />
 *
 * The last item (no `to`) renders as plain text (current page).
 */
export function Breadcrumbs({ items }: BreadcrumbsProps) {
  if (items.length <= 1) return null;

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-2xs text-ink-dim mb-3">
      {items.map((crumb, i) => {
        const isLast = i === items.length - 1;
        return (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && (
              <svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="opacity-40">
                <path d="M9 5l7 7-7 7" />
              </svg>
            )}
            {isLast || !crumb.to ? (
              <span className="font-medium text-ink truncate max-w-[200px]">{crumb.label}</span>
            ) : (
              <Link
                to={crumb.to}
                className="hover:text-ink transition-colors truncate max-w-[200px]"
              >
                {crumb.label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}
